import React from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";

// Compact SVG expected-loss curve
const LossCurve = ({ curve, height = 70 }) => {
  if (!curve || curve.length < 2) return null;
  const width = 280;
  const maxLoss = Math.max(1, ...curve.map((p) => p.expectedLoss));
  const maxHour = curve[curve.length - 1].hour;
  const pad = 4;
  const pts = curve
    .map((p) => {
      const x = pad + (p.hour / maxHour) * (width - 2 * pad);
      const y = height - pad - (p.expectedLoss / maxLoss) * (height - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <polyline
        points={`${pad},${height - pad} ${pts} ${width - pad},${height - pad}`}
        fill="rgba(255,138,61,0.12)"
        stroke="none"
      />
      <polyline points={pts} fill="none" stroke="#ff8a3d" strokeWidth="2" />
      <text x={pad + 2} y={12} fill="#8b98a5" fontSize="10">
        {maxLoss.toFixed(0)} cells
      </text>
      <text x={width - 34} y={height - 8} fill="#8b98a5" fontSize="10">
        +{maxHour}h
      </text>
    </svg>
  );
};

// Monte Carlo damage forecast panel (sidebar layout)
const ForecastPanel = ({
  forecast,
  running,
  progress,
  onRun,
  horizon,
  onHorizonChange,
  overlayVisible,
  onOverlayToggle,
  disabled,
}) => {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <InsightsIcon fontSize="small" color="warning" />
        <Typography variant="subtitle2">Damage Forecast</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Monte Carlo ensemble (12 runs) projected forward from the current fire
        state against the real wind timeline. Paints a burn-probability
        heatmap on the map.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TextField
          select
          size="small"
          label="Horizon"
          value={horizon}
          onChange={(e) => onHorizonChange(Number(e.target.value))}
          sx={{ width: 90 }}
        >
          <MenuItem value={24}>24 h</MenuItem>
          <MenuItem value={48}>48 h</MenuItem>
          <MenuItem value={72}>72 h</MenuItem>
        </TextField>
        <Button variant="contained" color="warning" onClick={onRun} disabled={disabled || running} size="small">
          {running ? "Forecasting…" : "Run Forecast"}
        </Button>
      </Stack>

      {running && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={(progress || 0) * 100} color="warning" />
          <Typography variant="caption" color="text.secondary">
            Ensemble {Math.round((progress || 0) * 100)}%
          </Typography>
        </Box>
      )}

      {forecast && !running && (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Expected loss in {forecast.horizonHours}h:{" "}
            <Box component="span" sx={{ color: "warning.main", fontWeight: 700 }}>
              {forecast.expectedInfraLoss.toFixed(1)} infra cells
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Value-weighted {forecast.expectedValueLoss.toFixed(0)} pts · {forecast.runs} runs
          </Typography>
          <LossCurve curve={forecast.lossCurve} />
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={
              <Switch size="small" checked={overlayVisible} onChange={(e) => onOverlayToggle(e.target.checked)} />
            }
            label={<Typography variant="body2">Risk heatmap on map</Typography>}
          />
        </Box>
      )}
    </Box>
  );
};

export default ForecastPanel;
